/**
 * Calendar Setup Page
 *
 * Onboarding step to connect Calendly after team creation
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Calendar, ArrowRight, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import {
  useCalendlyIntegration,
  useCalendlySchedules,
  useInitiateCalendlyOAuth,
  useSetDefaultSchedule,
} from '@/lib/integrations/calendly'

export function CalendarSetupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { data: integration, isLoading: isLoadingIntegration } = useCalendlyIntegration()
  const { data: schedules, isLoading: isLoadingSchedules } = useCalendlySchedules()
  const initiateOAuth = useInitiateCalendlyOAuth()
  const setDefaultSchedule = useSetDefaultSchedule()

  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null)

  const isConnected = integration?.status === 'connected'
  const justConnected = searchParams.get('calendly') === 'connected'

  const handleConnect = async () => {
    try {
      const { authorization_url } = await initiateOAuth.mutateAsync('/calendar-setup')
      window.location.href = authorization_url
    } catch (error) {
      console.error('Failed to initiate OAuth:', error)
    }
  }

  const handleContinue = async () => {
    // If a schedule is selected, set it as default
    if (selectedScheduleId) {
      try {
        await setDefaultSchedule.mutateAsync(selectedScheduleId)
      } catch (error) {
        console.error('Failed to set default schedule:', error)
      }
    }

    navigate('/pipeline')
  }

  const handleSkip = () => {
    navigate('/pipeline')
  }

  if (isLoadingIntegration) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
      {/* Success Message for Just Connected */}
      {justConnected && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Calendly connected successfully!</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
            {isConnected ? 'Choose your default schedule' : 'Connect your calendar'}
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400">
            {isConnected
              ? 'Select which schedule guests should use when booking recording times'
              : 'Let guests book recording times directly through Calendly'}
          </p>
        </div>
      </div>

      {/* Not Connected State */}
      {!isConnected && (
        <div className="space-y-6">
          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">
                  Automatic scheduling
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  Guests pick a time that works for both of you
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">
                  No back-and-forth emails
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  Your availability is always up to date
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">
                  Automatic updates
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  When a guest books, your pipeline updates automatically
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleConnect}
              disabled={initiateOAuth.isPending}
              className="w-full py-3 px-4 rounded-lg bg-[#FF6F00] hover:bg-[#FF6F00]/90 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {initiateOAuth.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5" />
                  Connect Calendly
                </>
              )}
            </button>

            <button
              onClick={handleSkip}
              className="w-full py-3 px-4 rounded-lg text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 font-medium transition-colors"
            >
              Skip for now
            </button>
          </div>

          <p className="text-xs text-center text-zinc-400">
            You can always connect Calendly later from Settings → Integrations
          </p>
        </div>
      )}

      {/* Connected State - Schedule Selection */}
      {isConnected && (
        <div className="space-y-6">
          {isLoadingSchedules ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            </div>
          ) : schedules && schedules.length > 0 ? (
            <div className="space-y-3">
              {schedules.map((schedule) => (
                <button
                  key={schedule.id}
                  onClick={() => setSelectedScheduleId(schedule.id)}
                  className={`
                    w-full p-4 rounded-lg border text-left transition-all
                    ${
                      selectedScheduleId === schedule.id
                        ? 'border-[#FF6F00] bg-[#FF6F00]/5 ring-1 ring-[#FF6F00]'
                        : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    {schedule.color && (
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: schedule.color }}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-zinc-900 dark:text-zinc-100">
                          {schedule.name}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-zinc-500">
                          <Clock className="w-4 h-4" />
                          {schedule.duration_minutes} min
                        </span>
                      </div>
                      {schedule.description && (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2">
                          {schedule.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-zinc-500 dark:text-zinc-400 mb-2">
                No event types found in your Calendly account
              </p>
              <p className="text-sm text-zinc-400">
                Create an event type in Calendly, then refresh this page
              </p>
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={setDefaultSchedule.isPending}
            className="w-full py-3 px-4 rounded-lg bg-[#FF6F00] hover:bg-[#FF6F00]/90 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {setDefaultSchedule.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue to Dashboard
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          {!selectedScheduleId && schedules && schedules.length > 0 && (
            <p className="text-xs text-center text-zinc-400">
              You can continue without selecting a default
            </p>
          )}
        </div>
      )}
    </div>
  )
}
